import { useCallback, useEffect, useRef } from 'react'

import { INITIAL_LOAD_FIELD, INITIAL_LOAD_FIELD_KEY } from '../../../constants'

export function useActions(
  value: Record<string, number | null>,
  apiChange: (value: Record<string, number | null>) => void,
) {
  const fieldValueRef = useRef(value)
  useEffect(() => {
    fieldValueRef.current = value
  }, [value])

  const onAddLoad = useCallback(() => {
    const hasEmptyField = Object.keys(value).some(val => val === INITIAL_LOAD_FIELD_KEY)
    if (!hasEmptyField) {
      // Merge the existing loads with a new initial load field
      const newLoad = { ...fieldValueRef.current, ...INITIAL_LOAD_FIELD } as Record<
        string,
        number | null
      >
      apiChange(newLoad)
    }
  }, [apiChange, value])

  const onUpdateSelect = useCallback(
    (newKey: string, index: number) => {
      // Update the key at the specified index
      const entries = Object.entries(value)
      entries[index] = [newKey, entries[index][1]]
      // Reconstruct the object from the updated entries
      const updated = Object.fromEntries(entries)

      apiChange(updated)
    },
    [apiChange, value],
  )

  const onUpdateValue = useCallback(
    (newValue: string, index: number) => {
      // Update the value at the specified index
      const entries = Object.entries(value)
      entries[index] = [entries[index][0], newValue === '' ? null : Number(newValue)]
      // Reconstruct the object from the updated entries
      const updated: Record<string, number | null> = {}
      entries.forEach(([k, v]) => {
        updated[k] = v
      })

      apiChange(updated)
    },
    [apiChange, value],
  )

  const onRemoveLoad = useCallback(
    (index: number) => {
      // Remove the entry at the specified index
      const entries = Object.entries(value)
      entries.splice(index, 1)
      // Reconstruct the object from the remaining entries
      const updated: Record<string, number | null> = {}
      entries.forEach(([k, v]) => {
        updated[k] = v
      })

      apiChange(updated)
    },
    [apiChange, value],
  )

  return {
    onAddLoad,
    onUpdateSelect,
    onUpdateValue,
    onRemoveLoad,
  }
}
